import { useEffect, useRef, useState } from 'preact/hooks';
import * as st from '../state/store';
import { entitlement, licenceNotice, removeFromDevice, tier, unlock } from '../entitlements/tier';
import { PRICES, STORE, aud, configured, loadStore } from '../entitlements/config';
import { track } from '../analytics';
import { BRAND } from '../brand';

const TIER_NAME = { supporter: 'Supporter', school: 'School licence' } as const;

/** Support, buy or unlock (F13). Opened only when the reader asks: menu, a locked tour, export or offline. */
export function UnlockDialog({ initialKey }: { initialKey?: string }) {
  const [key, setKey] = useState(initialKey ?? '');
  const [status, setStatus] = useState<{ kind: 'idle' | 'working' | 'ok' | 'error'; text?: string }>({ kind: 'idle' });
  const [, setReady] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const close = () => (st.panel.value = null);
  const ent = entitlement.value;
  const unlocked = tier.value !== 'none' && ent;

  const submit = async (k = key) => {
    if (!k.trim()) return;
    setStatus({ kind: 'working' });
    const r = await unlock(k);
    if (r.ok) {
      track('unlock', { tier: r.entitlement.tier });
      setStatus({ kind: 'ok', text: `Unlocked. Thank you! ${TIER_NAME[r.entitlement.tier]} perks are on for this device.` });
    } else setStatus({ kind: 'error', text: r.message });
  };

  useEffect(() => {
    track('supporter_view', { source: initialKey ? 'magic_link' : 'menu' });
    void loadStore().then(() => setReady(true));
    if (initialKey) void submit(initialKey);
    else input.current?.focus();
    // Runs once when the dialog opens; a magic-link key is submitted exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = (url: string, label: string, primary = false) =>
    url ? (
      <a class={`btn ${primary ? 'primary' : ''}`} href={url} target="_blank" rel="noopener" data-testid="checkout-link">{label}</a>
    ) : (
      <button type="button" class={`btn ${primary ? 'primary' : ''}`} disabled title="Coming soon">{label}</button>
    );

  return (
    <div class="overlay" onClick={(e) => e.target === e.currentTarget && close()} onKeyDown={(e) => e.key === 'Escape' && (e.stopPropagation(), close())}>
      <div class="dialog unlock-dialog" role="dialog" aria-modal="true" aria-labelledby="unlock-title" data-testid="unlock-dialog">
        <div class="dialog-head">
          <h2 id="unlock-title">{unlocked ? 'Thank you' : `Support ${BRAND.name}`}</h2>
          <button type="button" class="btn ghost icon" aria-label="Close" onClick={close}>✕</button>
        </div>

        {unlocked ? (
          <section data-testid="unlock-status" data-tier={tier.value}>
            <p>
              <strong>{TIER_NAME[ent.tier]}</strong> perks are on for this device{ent.productName ? ` (${ent.productName})` : ''}: the whole atlas offline, supporter tours and 4K image export.
            </p>
            <p class="fine">Last checked {new Date(ent.validatedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}. It keeps working offline for 30 days between checks.</p>
            <div class="dialog-actions">
              {STORE.portal && <a class="btn" href={STORE.portal} target="_blank" rel="noopener" data-testid="manage-subscription">Manage subscription</a>}
              <button type="button" class="btn ghost" onClick={() => void removeFromDevice().then(() => setStatus({ kind: 'idle', text: 'Removed from this device. Your key still works on your other devices.' }))} data-testid="remove-licence">
                Remove from this device
              </button>
            </div>
          </section>
        ) : (
          <>
            <p>{BRAND.name} is free and stays free: no adverts, no accounts, no paywalled cards. Supporters keep it going and get a few thank-yous: the whole atlas offline, supporter tours and 4K image export.</p>
            {licenceNotice.value && <p class="notice" data-testid="licence-notice">{licenceNotice.value}</p>}
            <div class="dialog-actions buy">
              {checkout(STORE.checkout.oneOff, `${aud(PRICES.oneOff)} once`, true)}
              {checkout(STORE.checkout.monthly, `${aud(PRICES.monthly)} a month`)}
            </div>
            <p class="fine">
              Prices in Australian dollars; checkout shows your local currency and any tax. You’ll get a licence key by email. No account, no password.
              {!configured() && ' Payments open soon.'} <a href="/supporter/">What supporters get</a> · Schools: see <a href="/teachers/">licences for schools</a>.
            </p>
            <form class="unlock-form" onSubmit={(e) => (e.preventDefault(), void submit())}>
              <label for="licence-key">Already have a key?</label>
              <div class="row">
                <input id="licence-key" ref={input} type="text" autocomplete="off" spellcheck={false} placeholder="Paste your licence key" value={key} onInput={(e) => setKey((e.target as HTMLInputElement).value)} data-testid="licence-input" />
                <button type="submit" class="btn primary" disabled={status.kind === 'working' || !key.trim()} data-testid="licence-submit">
                  {status.kind === 'working' ? 'Checking…' : 'Unlock'}
                </button>
              </div>
            </form>
          </>
        )}
        <p class={`status ${status.kind === 'error' ? 'error' : ''}`} role="status" aria-live="polite" data-testid="unlock-message">{status.text ?? ''}</p>
      </div>
    </div>
  );
}
